import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatRM, formatDateTime, STATUSES, BACKEND_URL, waLink, waJobMessage, waAgentJobMessage } from "@/lib/api";
import { JobItemsList } from "@/components/OrderItems";
import { DeptPicPanel } from "@/components/DeptPicPanel";
import { Countdown } from "@/components/Countdown";
import { QcChecklist, qcComplete } from "@/components/QcChecklist";
import { StatusBadge, AgentBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Upload, DollarSign, Image as ImageIcon,
  FileText, History, MessageSquare, Flame, ExternalLink, Printer, MessageCircle
} from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [noteVisibility, setNoteVisibility] = useState("internal");
  const [pay, setPay] = useState({ amount: 0, payment_type: "deposit", method: "bank_transfer", reference: "" });
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const load = () => api.get(`/jobs/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)); }, [id]);

  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  const { job, payments, artworks, history, notes, files } = data;
  const canSales = hasPerm("sales");
  const paid = (job.deposit_paid || 0) + (job.balance_paid || 0);
  const outstanding = (job.total_price || 0) - paid;

  const updateJob = async (updates) => {
    try {
      await api.patch(`/jobs/${id}`, updates);
      await load();
      toast.success("Job updated");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/jobs/${id}/notes`, { text: noteText, visibility: noteVisibility });
    setNoteText("");
    load();
  };

  const addPayment = async () => {
    if (!pay.amount || pay.amount <= 0) return toast.error("Amount required");
    await api.post(`/jobs/${id}/payments`, { ...pay, amount: Number(pay.amount) });
    setPay({ amount: 0, payment_type: "deposit", method: "bank_transfer", reference: "" });
    load();
    toast.success("Payment recorded");
  };

  const uploadArtwork = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingArtwork(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: fdata } = await api.post(`/files/upload?job_id=${id}`, fd);
      await api.post(`/jobs/${id}/artworks`, { file_path: fdata.storage_path, filename: fdata.original_filename, notes: "" });
      toast.success("Artwork uploaded");
      load();
    } catch { toast.error("Upload failed"); } finally { setUploadingArtwork(false); e.target.value = ""; }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/files/upload?job_id=${id}`, fd);
      toast.success("File uploaded");
      load();
    } catch { toast.error("Upload failed"); } finally { setUploadingFile(false); e.target.value = ""; }
  };

  const approvalUrl = `${window.location.origin}/approve/${job.approval_token}`;
  const copyApprovalLink = () => {
    navigator.clipboard.writeText(approvalUrl);
    toast.success("Approval link copied!");
  };

  return (
    <div className="space-y-4" data-testid="job-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <div>
            <div className="flex items-center gap-2">
              {job.priority === "urgent" && <Flame className="w-4 h-4 text-orange-500" />}
              <h1 className="text-2xl font-display font-bold font-mono">{job.job_number}</h1>
              <StatusBadge status={job.status} />
              <AgentBadge job={job} />
            </div>
            <p className="text-sm text-muted-foreground">{job.customer_name} • {job.product}{job.agent_name && <span className="ml-2 text-amber-600 font-semibold" data-testid="job-agent-tag">• Agent: {job.agent_name}</span>}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav(`/jobs/${id}/print`)} data-testid="print-job-sheet">
            <Printer className="w-4 h-4 mr-1" /> Job Sheet
          </Button>
          <Button variant="outline" onClick={() => nav(`/labels?ids=${id}`)} data-testid="print-thermal-label">
            <Printer className="w-4 h-4 mr-1" /> Thermal Label
          </Button>
          <WhatsAppButton phone={job.customer_phone} message={waJobMessage(job)} label="WA Customer" testid="wa-customer-header" className="h-10 px-3 text-sm" />
          <WhatsAppButton phone={job.agent_phone} message={waAgentJobMessage(job)} label="WA Agent" testid="wa-agent-header" className="h-10 px-3 text-sm" />
          <Button variant="outline" onClick={copyApprovalLink} data-testid="copy-approval-link">
            <Copy className="w-4 h-4 mr-1" /> Approval Link
          </Button>
          <a href={approvalUrl} target="_blank" rel="noreferrer">
            <Button variant="outline"><ExternalLink className="w-4 h-4 mr-1" /> Open</Button>
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left - main details */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold">Status & Assignment</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={job.status} onValueChange={(v) => updateJob({ status: v })}>
                  <SelectTrigger data-testid="job-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Current PIC (auto)</Label>
                <div className="h-9 flex items-center px-3 border rounded-md text-sm bg-muted/40 font-medium" data-testid="job-current-pic">{job.current_pic_name || <span className="text-muted-foreground">Not updated yet</span>}</div>
              </div>
              <div>
                <Label className="text-xs">Time Remaining</Label>
                <div className="h-9 flex items-center" data-testid="job-countdown-box"><Countdown job={job} /> {!job.due_at || (job.due_hours ?? 99) > 24 ? <span className="text-sm text-muted-foreground">{job.deadline || "-"}</span> : null}</div>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="specs" className="w-full">
            <TabsList className={`grid ${canSales ? "grid-cols-5" : "grid-cols-4"} w-full`}>
              <TabsTrigger value="specs" data-testid="tab-specs">Specs</TabsTrigger>
              <TabsTrigger value="artwork" data-testid="tab-artwork">Artwork</TabsTrigger>
              {canSales && <TabsTrigger value="payment" data-testid="tab-payment">Payment</TabsTrigger>}
              <TabsTrigger value="files" data-testid="tab-files">Files</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
            </TabsList>

            <TabsContent value="specs">
              <Card className="p-5 space-y-3 text-sm">
                {job.items?.length > 1 ? <><div className="text-xs font-mono uppercase text-muted-foreground">Products ({job.items.length} items)</div><JobItemsList job={job} showPrices={canSales} /></> : <Row k="Product" v={job.product} />}
                {job.variant_name && <Row k="Variant" v={`${job.variant_name}${Object.keys(job.variant_attributes || {}).length ? " — " + Object.entries(job.variant_attributes).map(([k, v]) => `${k}: ${v}`).join(", ") : ""}`} />}
                {Object.entries(job.specs || {}).map(([k, v]) => <Row key={k} k={k} v={v} />)}
                {job.delivery_method && <Row k="Delivery" v={`${job.delivery_method}${job.delivery_fee ? ` (RM ${Number(job.delivery_fee).toFixed(2)})` : ""}`} />}
                {!Object.keys(job.specs || {}).length && <><Row k="Material" v={job.material || "-"} />
                <Row k="Finishing" v={job.finishing || "-"} />
                <Row k="Dimensions" v={job.dimensions || "-"} mono /></>}
                <Row k="Quantity" v={job.quantity} mono />
                {canSales && <Row k="Unit Price" v={formatRM(job.unit_price)} mono />}
                {canSales && <Row k="Total Price" v={formatRM(job.total_price)} mono bold />}
                <Row k="Source" v={job.source} />
                <Row k="Deadline" v={job.deadline || "-"} />
                {job.process_time && <Row k="Process Time (excl. postage)" v={`${job.process_time} — ${job.process_turnaround || ""}`} />}
                {canSales && job.surcharge_pct > 0 && <Row k={`Process Time Surcharge (${job.surcharge_pct}%)`} v={`+${formatRM(job.surcharge_amount)}`} mono />}
                <Row k="Created by" v={job.created_by} />
                <Row k="Notes" v={job.notes || "-"} />
              </Card>
            </TabsContent>

            <TabsContent value="artwork">
              <Card className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Artwork Versions</h3>
                  <label className="cursor-pointer">
                    <input type="file" hidden accept="image/*,.pdf" onChange={uploadArtwork} data-testid="upload-artwork" />
                    <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded">
                      <Upload className="w-4 h-4" /> {uploadingArtwork ? "Uploading..." : "Upload New Version"}
                    </div>
                  </label>
                </div>
                <div className="space-y-3">
                  {artworks.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No artwork yet</div>}
                  {artworks.map((a) => (
                    <div key={a.id} className="border rounded-lg p-3 flex items-start gap-3">
                      <ImageIcon className="w-8 h-8 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono font-semibold text-sm">Version {a.version} — {a.filename}</div>
                        {a.item_product && <div className="text-xs text-amber-700 font-semibold" data-testid={`artwork-item-tag-${a.id}`}>Item {a.item_index + 1}: {a.item_product}</div>}
                        <div className="text-xs text-muted-foreground">{formatDateTime(a.created_at)} by {a.uploaded_by}</div>
                        <div className="mt-1">
                          <Badge variant={a.status === "approved" ? "default" : a.status === "revision_requested" ? "destructive" : "secondary"}>
                            {a.status}
                          </Badge>
                        </div>
                        {a.customer_comments && <div className="text-xs mt-1 italic">"{a.customer_comments}"</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            {canSales && <TabsContent value="payment">
              <Card className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <StatBox label="Total" value={formatRM(job.total_price)} />
                  <StatBox label="Paid" value={formatRM(paid)} color="text-emerald-600" />
                  <StatBox label="Outstanding" value={formatRM(outstanding)} color="text-rose-600" />
                </div>
                <div className="border rounded-lg p-3 space-y-3">
                  <div className="font-semibold text-sm">Record Payment</div>
                  <div className="grid md:grid-cols-4 gap-2">
                    <Input type="number" placeholder="Amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="pay-amount" />
                    <Select value={pay.payment_type} onValueChange={(v) => setPay({ ...pay, payment_type: v })}>
                      <SelectTrigger data-testid="pay-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="deposit">Deposit</SelectItem><SelectItem value="balance">Balance</SelectItem></SelectContent>
                    </Select>
                    <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                      <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="online">Online (FPX)</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Reference" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} />
                  </div>
                  <Button onClick={addPayment} data-testid="pay-submit" className="bg-cyan-600 hover:bg-cyan-700"><DollarSign className="w-4 h-4 mr-1" /> Record Payment</Button>
                </div>
                <div className="divide-y">
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-2 text-sm">
                      <div>
                        <div className="font-semibold">{formatRM(p.amount)} • {p.payment_type}</div>
                        <div className="text-xs text-muted-foreground">{p.method} • {p.reference} • {formatDateTime(p.created_at)}</div>
                      </div>
                      <Badge variant="secondary">{p.recorded_by}</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>}

            <TabsContent value="files">
              <Card className="p-5 space-y-4">
                <label className="cursor-pointer">
                  <input type="file" hidden onChange={uploadFile} data-testid="upload-file" />
                  <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded">
                    <Upload className="w-4 h-4" /> {uploadingFile ? "Uploading..." : "Upload File"}
                  </div>
                </label>
                <div className="divide-y">
                  {files.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">No files uploaded yet</div>}
                  {files.map((f) => (
                    <div key={f.id} className="flex items-center justify-between py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 shrink-0" />
                        <div className="truncate">
                          <div className="font-medium truncate">{f.original_filename}</div>
                          <div className="text-xs text-muted-foreground">{formatDateTime(f.created_at)} • {f.uploaded_by}</div>
                        </div>
                      </div>
                      <a href={`${BACKEND_URL}/api/files/${f.id}/download`} target="_blank" rel="noreferrer" className="text-cyan-600 hover:underline text-xs">View</a>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="history">
              <Card className="p-5 space-y-2">
                {history.map((h) => (
                  <div key={h.id} className="flex items-start gap-3 text-sm py-2 border-b last:border-0">
                    <History className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <div><span className="font-semibold">{h.user_name}</span> <span className="text-muted-foreground">— {h.action}</span></div>
                      {h.detail && <div className="text-xs text-muted-foreground">{h.detail}</div>}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">{formatDateTime(h.created_at)}</div>
                  </div>
                ))}
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right - customer + notes */}
        <div className="space-y-4">
          <DeptPicPanel job={job} />
          {(job.status === "Preparing" || job.qc_checklist) && (
            <Card className="p-4" data-testid="job-qc-card">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-bold text-sm">QC Checklist</h3>
                {qcComplete(job.qc_checklist) ? <Badge className="bg-emerald-600">Passed</Badge> : <Badge variant="secondary">Incomplete</Badge>}
              </div>
              <QcChecklist value={job.qc_checklist || {}} onChange={(v) => updateJob({ qc_checklist: v })} disabled={job.status !== "Preparing"} compact />
              {job.qc_checked_by && <div className="text-xs text-muted-foreground mt-2">Checked by {job.qc_checked_by} • {formatDateTime(job.qc_checked_at)}</div>}
              {job.status === "Preparing" && <p className="text-xs text-muted-foreground mt-2">All items must be ticked before the status can be changed to "Ready for Collection".</p>}
            </Card>
          )}
          {job.handover_note?.text && (
            <Card className="p-4 border-amber-300 bg-amber-50 dark:bg-amber-950/30" data-testid="job-handover-note">
              <div className="text-xs font-mono uppercase text-amber-700 dark:text-amber-400 mb-1">Handover Note → {job.handover_note.to_status}</div>
              <div className="text-sm">{job.handover_note.text}</div>
              <div className="text-xs text-muted-foreground mt-1">{job.handover_note.by} • {formatDateTime(job.handover_note.at)}</div>
            </Card>
          )}
          <Card className="p-5">
            <h3 className="font-display font-bold mb-3">Customer</h3>
            <div className="space-y-1 text-sm">
              <div className="font-semibold">{job.customer_name}</div>
              <div className="text-muted-foreground">{job.customer_phone || "-"}</div>
              <div className="text-muted-foreground">{job.customer_email || "-"}</div>
            </div>
            {job.customer_phone && (
              <a href={waLink(job.customer_phone, waJobMessage(job))} target="_blank" rel="noreferrer" data-testid="whatsapp-customer-btn">
                <Button size="sm" className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp Update
                </Button>
              </a>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Internal Notes</h3>
            <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
              {notes.map((n) => (
                <div key={n.id} className={`p-2 rounded text-sm ${n.visibility === "agent" ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900" : "bg-muted/50"}`}>
                  <div className="text-xs text-muted-foreground mb-1">{n.user_name} • {formatDateTime(n.created_at)} {n.visibility === "agent" && <span className="text-amber-600 font-semibold">• Agent</span>}</div>
                  <div>{n.text}</div>
                </div>
              ))}
            </div>
            <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Write a note..." data-testid="note-input" />
            {job.agent_id && (
              <Select value={noteVisibility} onValueChange={setNoteVisibility}>
                <SelectTrigger className="mt-2" data-testid="note-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal note (staff only)</SelectItem>
                  <SelectItem value="agent">Message to Agent</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button onClick={addNote} size="sm" className="mt-2 w-full bg-cyan-600 hover:bg-cyan-700" data-testid="note-submit">Add Note</Button>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v, mono, bold }) => (
  <div className="flex justify-between py-1 border-b last:border-0">
    <span className="text-muted-foreground">{k}</span>
    <span className={`${mono ? "font-mono" : ""} ${bold ? "font-bold" : ""}`}>{v}</span>
  </div>
);

const StatBox = ({ label, value, color = "" }) => (
  <div className="p-3 border rounded-lg">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-lg font-display font-bold font-mono ${color}`}>{value}</div>
  </div>
);
